import React, { useState } from 'react';
import classNames from 'classnames';
import { segmentCase } from '@guildeducationinc/catalog-tools';
import { Button, Typography } from '@guildeducationinc/recess';
import ExpandMore from '@guildeducationinc/recess/icons/ExpandMoreIcon';
import { GetMemberProgramQuery } from 'src/graphqlTypes/catalogServiceTypes';
import { About } from 'src/components/ProgramDetails/Institution/About';
import { Reasons } from 'src/components/ProgramDetails/Institution/Reasons';
import { Skeleton } from 'src/components/UserFacetSkeletonLoader';
import { trackEvent } from 'src/services/analytics';
import styles from './Institution.module.scss';

interface Props {
	program: GetMemberProgramQuery['memberProgram'];
	loading: boolean;
}

export const Institution: React.FC<Props> = ({ program, loading }) => {
	const [showMore, setShowMore] = useState(false);
	const [showImage, setShowImage] = useState(true);

	const handleClick = () => {
		const buttonText = showMore ? 'show_less' : 'show_more';
		trackEvent('Clickable', {
			tag: `institution_${buttonText}`,
			text: `${segmentCase(buttonText)}`,
			type: 'button',
		});
		setShowMore(!showMore);
	};

	return (
		<section
			aria-label='institution'
			className={styles.sectionContainer}
			data-testid='program-detail-institution'
			data-title='Institution'
			id='detail-institution'
			tabIndex={-1}
		>
			<Typography className={styles.title} variant='h2'>
				Institution
			</Typography>

			<Skeleton dataLoading={loading} height={270}>
				{program.studentFacingAcademicPartner.overview.length < 200 || showMore ? (
					<>
						<About
							program={program}
							setShowImage={setShowImage}
							showImage={showImage}
							showMore={showMore}
						/>
						<Reasons program={program} />
					</>
				) : (
					<About
						program={program}
						setShowImage={setShowImage}
						showImage={showImage}
						showMore={showMore}
					/>
				)}
				<Button
					appearance='ghost'
					aria-expanded={showMore}
					className={classNames(
						{ [styles.showMoreBtnExpanded]: showMore },
						{ [styles.showMoreBtnCollapsed]: !showMore },
						{ [styles.showMoreBtnNoImage]: !showImage }
					)}
					hidden={program.studentFacingAcademicPartner.overview.length < 200}
					icon={
						<ExpandMore className={classNames({ [styles.showMoreIcon]: showMore })} size={24} />
					}
					iconDirection='right'
					onClick={handleClick}
					testid='show-more-button'
				>
					{showMore ? 'Show less' : 'Show more'}
				</Button>
			</Skeleton>
		</section>
	);
};
